import { Schema, Effect } from "effect";
import { JsonUnknown } from "../json-unknown.js";
import {
  RealtimeMetricDefinition,
  RealtimePublicMetricEvidence,
  RealtimeScoringDefinition,
  RealtimeWorkforceEvidence,
  MoneyValue,
  PredicateValue,
} from "@bloodline-packages/data-transferring-definitions";

// import { MasterData } from '@bloodline-packages/data-transferring-definitions';

export const HeatFilterV3ResponseSchema = Schema.Struct({
  heatFilterId: Schema.String,
  name: Schema.String,
  revision: Schema.Number,
  /** The active source of filter results during the realtime migration. */
  executionMode: Schema.Literals(["legacy_live", "metric_v1", "manual_sql"]),
  /** The staged metric-v1 definition, if one exists. */
  definition: Schema.NullOr(RealtimeScoringDefinition),
  /**
   * Persisted canonical-canary approval eligible to authorize a metric-v1
   * transition at this exact definition, configuration, and revision.
   */
  canonicalCanaryApprovedRevision: Schema.NullOr(Schema.Number).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => null)),
    Schema.withConstructorDefault(Effect.sync(() => null))
  ),
  // v3 decoded a missing key here because `Unknown` accepts `undefined`; v4 Structs require the key.
  configuration: Schema.OptionFromOptionalNullOr(JsonUnknown, { onNoneEncoding: null }),
  sqlConfiguration: Schema.OptionFromNullOr(Schema.String),
  isPrivate: Schema.Boolean,
  clientId: Schema.OptionFromNullOr(Schema.String),
});

export const HeatFilterResultsResponseSchema = Schema.Struct({
  metadata: Schema.Struct({
    count: Schema.Number,
    limit: Schema.Number,
    offset: Schema.Number,
    executionMode: Schema.Literals(["legacy_live", "metric_v1", "manual_sql"]),
    /**
     * Whether these results were produced at all. A staged filter has a metric-v1
     * definition but no runnable legacy configuration, so it returns no rows
     * rather than failing as if the filter did not exist. Optional so older
     * deployments remain decodable while this field rolls out.
     */
    status: Schema.Literals(["ready", "staged_not_active"]).pipe(
      Schema.withDecodingDefaultType(Effect.sync(() => "ready" as const)),
      Schema.withConstructorDefault(Effect.sync(() => "ready" as const))
    ),
  }),
  data: Schema.Array(JsonUnknown), // TODO: implement old MasterData.Effect.MasterDataSchemaWithScore),
});

export const HeatFilterIdParamSchema = Schema.Struct({
  heatFilterId: Schema.String.pipe(Schema.check(Schema.isMinLength(1))),
});

/** Payload for staging a validated metric-v1 definition. */
export const RealtimeScoringDefinitionUpdateSchema = Schema.Struct({
  definition: RealtimeScoringDefinition,
  expectedRevision: Schema.Number.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThan(0))
  ),
});

/** The backend-owned allow-list for authoring a metric-v1 definition. */
export const RealtimeScoringMetricCatalogResponseSchema = Schema.Struct({
  version: Schema.Literal("v1"),
  metrics: Schema.Array(RealtimeMetricDefinition),
});

/** Read-only readiness for the indexes needed by direct realtime source queries. */
export const RealtimeScoringSourceIndexReadinessResponseSchema = Schema.Struct({
  ready: Schema.Boolean,
  missingIndexNames: Schema.Array(Schema.String),
});

/** One explainable decision from the read-only legacy-to-V1 migration assistant. */
export const RealtimeScoringMigrationFindingResponseSchema = Schema.Struct({
  legacyPath: Schema.String,
  disposition: Schema.Literals(["direct", "semantic_change", "unsupported"]),
  message: Schema.String,
  suggestedMetric: Schema.optional(Schema.String),
});

/**
 * A non-persistent V1 migration assessment for an existing structured filter.
 * The returned definition is only a review draft; this endpoint never saves or
 * activates it.
 */
export const RealtimeScoringMigrationAssessmentResponseSchema = Schema.Struct({
  findings: Schema.Array(RealtimeScoringMigrationFindingResponseSchema),
  suggestedDefinition: Schema.optional(RealtimeScoringDefinition),
  requiresManualTranslation: Schema.Boolean,
});

/** Read-only rollout classification for one filter in the migration candidate inventory. */
export const RealtimeScoringMigrationCandidateResponseSchema = Schema.Struct({
  heatFilterId: Schema.String,
  name: Schema.String,
  revision: Schema.Number,
  executionMode: Schema.Literals(["legacy_live", "metric_v1", "manual_sql"]),
  status: Schema.Literals([
    "canonical_candidate",
    "definition_review",
    "semantic_review",
    "manual_translation",
  ]),
  stagedDefinition: Schema.Boolean,
  canonicalCanaryApproved: Schema.Boolean,
  findingCount: Schema.Number,
});

/** A client-visible, non-mutating inventory for selecting V1 migration candidates. */
export const RealtimeScoringMigrationCandidateInventoryResponseSchema = Schema.Struct({
  candidates: Schema.Array(RealtimeScoringMigrationCandidateResponseSchema),
});

/**
 * The persisted metric-v1 definition metadata returned after a successful staged update.
 * An active metric-v1 filter is returned to legacy_live before the definition is
 * replaced, while manual SQL remains an independent execution path.
 */
export const RealtimeScoringDefinitionResponseSchema = Schema.Struct({
  heatFilterId: Schema.String,
  definition: RealtimeScoringDefinition,
  executionMode: Schema.Literals(["legacy_live", "manual_sql"]),
  revision: Schema.Number,
});

/** Enables or rolls back the runtime query path for a staged definition. */
export const RealtimeScoringExecutionModeUpdateSchema = Schema.Struct({
  executionMode: Schema.Literals(["legacy_live", "metric_v1"]),
  expectedRevision: Schema.Number.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThan(0))
  ),
});

/** The revisioned runtime mode returned after a successful cutover or rollback. */
export const RealtimeScoringExecutionModeResponseSchema = Schema.Struct({
  heatFilterId: Schema.String,
  executionMode: Schema.Literals(["legacy_live", "metric_v1"]),
  revision: Schema.Number,
});

/** Explicit acknowledgement that a source-equivalent canary has been reviewed. */
export const RealtimeScoringCanonicalCanaryApprovalUpdateSchema = Schema.Struct({
  expectedRevision: Schema.Number.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThan(0))
  ),
});

/** The exact staged definition revision approved for the guarded metric-v1 cutover. */
export const RealtimeScoringCanonicalCanaryApprovalResponseSchema = Schema.Struct({
  heatFilterId: Schema.String,
  revision: Schema.Number,
});

/** A company score evaluated from the current raw source facts. */
export const RealtimeScoringCompanyResponseSchema = Schema.Struct({
  orgnr: Schema.String,
  companyName: Schema.NullOr(Schema.String),
  score: Schema.Number,
  matchedRuleIds: Schema.Array(Schema.String),
  /** Live source values for the metrics evaluated by this definition. */
  metricValues: Schema.Record(Schema.String, Schema.NullOr(PredicateValue)),
  /** Source and date provenance for each live metric value; internal record IDs are never exposed. */
  metricEvidence: Schema.optional(
    Schema.Record(Schema.String, Schema.NullOr(RealtimePublicMetricEvidence))
  ),
  /** Present when the definition evaluated a workforce-backed metric. */
  workforceEvidence: Schema.optional(Schema.NullOr(RealtimeWorkforceEvidence)),
});

/** A pageable, live preview of a staged realtime scoring definition. */
export const RealtimeScoringResultsResponseSchema = Schema.Struct({
  metadata: Schema.Struct({
    /** The filter revision whose staged definition was evaluated. */
    revision: Schema.Number,
    count: Schema.Number,
    limit: Schema.Number,
    offset: Schema.Number,
  }),
  data: Schema.Array(RealtimeScoringCompanyResponseSchema),
});

/** A score difference found while evaluating legacy scoring on a metric-v1 cohort. */
export const RealtimeScoringShadowScoreMismatchResponseSchema = Schema.Struct({
  orgnr: Schema.String,
  legacyScore: Schema.Number,
  realtimeScore: Schema.Number,
});

/** One bounded per-field diagnostic between legacy master-view and raw V1 values. */
export const RealtimeScoringSemanticComparisonResponseSchema = Schema.Struct({
  orgnr: Schema.String,
  legacyPath: Schema.String,
  metric: Schema.String,
  status: Schema.Literals(["match", "value_mismatch", "not_comparable"]),
  legacyValue: Schema.optional(
    Schema.NullOr(Schema.Union([Schema.String, Schema.Number, Schema.Boolean, MoneyValue]))
  ),
  realtimeValue: Schema.optional(
    Schema.NullOr(Schema.Union([Schema.String, Schema.Number, Schema.Boolean, MoneyValue]))
  ),
  realtimeWorkforceEvidence: Schema.optional(Schema.NullOr(RealtimeWorkforceEvidence)),
  reason: Schema.optional(Schema.String),
});

/**
 * A non-persistent, like-for-like comparison between one staged metric-v1
 * result page and legacy scoring over those exact organizations.
 */
export const RealtimeScoringShadowResultsResponseSchema = Schema.Struct({
  metadata: Schema.Struct({
    comparisonScope: Schema.Literal("realtime-page-candidate-cohort"),
    /**
     * Makes the source boundary explicit: this is a diagnostic canary between
     * a materialized legacy projection and the raw metric-v1 compiler.
     */
    sourceSemantics: Schema.optional(
      Schema.Struct({
        legacyDataOrigin: Schema.Literal("master-materialized-view"),
        realtimeDataOrigin: Schema.Literal("raw-metric-v1"),
        interpretation: Schema.Literal("cross-source-semantic-canary"),
      })
    ),
    revision: Schema.Number,
    /** Number of V1-eligible companies across all result pages. */
    realtimeEligibleCount: Schema.Number,
    /** Number of V1 companies in this compared page. */
    candidateCount: Schema.Number,
    legacyEligibleCount: Schema.Number,
    onlyRealtimeCount: Schema.Number,
    scoreMismatchCount: Schema.Number,
    /**
     * Optional page-bounded semantic diagnostic totals for this filter revision.
     * They describe source values, not score equivalence or cutover readiness.
     */
    semanticSummary: Schema.optional(
      Schema.Struct({
        matchCount: Schema.Number,
        valueMismatchCount: Schema.Number,
        notComparableCount: Schema.Number,
      })
    ),
    limit: Schema.Number,
    offset: Schema.Number,
  }),
  /** V1-eligible companies excluded by the legacy filter or missing from its source view. */
  onlyRealtimeOrgnrs: Schema.Array(Schema.String),
  scoreMismatches: Schema.Array(RealtimeScoringShadowScoreMismatchResponseSchema),
  /** Optional so older deployments remain decodable while this diagnostic rolls out. */
  semanticComparisons: Schema.optional(
    Schema.Array(RealtimeScoringSemanticComparisonResponseSchema)
  ),
});

export const ZapierFilterResultResponseSchema = Schema.Array(
  Schema.StructWithRest(
    Schema.Struct({
      id: Schema.String,
      // TODO: Add MasterData.Effect.MasterDataSchemaWithScore.fields when available
    }),
    [Schema.Record(Schema.String, JsonUnknown)]
  )
);
